// Utils
import { prop } from 'lodash/fp';
import measurementsRegistry from '../../components/measurementsStore/measurementsRegistry';
import { gridPointsToPixels } from '../../utils/grid/gridUtils';
import { getWindowWidth } from '../../utils/domUtil';
import { getElementsBoundingRect } from '../canvasUtils';
import { BOARD_PADDING_GRID_POINTS, flattenCanvasLayers, getToolbarBoundingRect } from './canvasScaleCommonUtils';

// Constants
import { EXPORT_ROOT_ELEMENT_ID } from '../../workspace/export/exportConstants';

const MAXIMUM_SCALING_FACTOR = 1.3;

export const initialiseScale = () => {
    // Remove the 'content-visibility: auto' style to pre-render the elements before the scale transition
    const domElements = document.querySelectorAll('.Element');

    for (const domElement of domElements) {
        domElement.style.contentVisibility = 'visible';
    }

    // When scaling add both the animation and scaled out classes
    const workspacePositioner = document.querySelector('.WorkspacePositioner');
    const toolbarRect = getToolbarBoundingRect();
    const toolbarWidth = (toolbarRect && toolbarRect.width) || 0;

    const canvasViewportNode = measurementsRegistry.getCanvasViewportDomElement();
    const scrollableContainer = document.getElementById(EXPORT_ROOT_ELEMENT_ID);

    const initialCanvasScrollLeft = canvasViewportNode.scrollLeft;
    const initialCanvasScrollTop = canvasViewportNode.scrollTop;

    workspacePositioner.classList.add('scale-animation', 'scaled');

    const initialTranslateX = toolbarWidth - initialCanvasScrollLeft;
    const initialTranslateY = -initialCanvasScrollTop;

    // Clear the current scroll position of the canvas and translate the scrollable area so that there's no
    // noticeable difference from when the Canvas was scrolled vs when the scrollable container is translated
    canvasViewportNode.scrollLeft = 0;
    canvasViewportNode.scrollTop = 0;
    scrollableContainer.style.transform = `translate3d(${initialTranslateX}px, ${initialTranslateY}px, 0) scale(${1})`;

    flattenCanvasLayers();
};

export const setScaledTransitionDestinationStyles = ({
    canvasSize,
    gridSize,
    dispatchGetMeasurementsMap,
    dispatchGetCanvasElements,
    returnCanvasScroll,
}) => {
    const measurements = dispatchGetMeasurementsMap();
    const canvasElements = dispatchGetCanvasElements();
    const elementBoundingRect = getElementsBoundingRect(canvasElements, measurements, gridSize, {
        x: prop('left', returnCanvasScroll) || 0,
        y: prop('top', returnCanvasScroll) || 0,
    });

    if (!elementBoundingRect || !elementBoundingRect.width || !elementBoundingRect.height) return;

    const scrollableContainer = document.getElementById(EXPORT_ROOT_ELEMENT_ID);

    const canvasViewportNode = measurementsRegistry.getCanvasViewportDomElement();
    const canvasContainerRect = canvasViewportNode.getBoundingClientRect();

    // TRANSFORM ORIGIN
    // Transform origin needs to be the center of the elements bounding rectangle
    const transformOriginX = elementBoundingRect.width / 2 + elementBoundingRect.left;
    const transformOriginY = elementBoundingRect.height / 2 + elementBoundingRect.top;

    // Ensure the elements have some padding around them
    const boardPadding = gridPointsToPixels(BOARD_PADDING_GRID_POINTS, gridSize);
    const totalBoardWidth = elementBoundingRect.width + 2 * boardPadding;
    const totalBoardHeight = elementBoundingRect.height + 2 * boardPadding;

    // SCALING
    // The width of the canvas will fill the window once the styles are applied
    const windowWidth = getWindowWidth();
    const widthRatio = windowWidth / totalBoardWidth;
    // The height of the canvas will remain as is
    const heightRatio = canvasContainerRect.height / totalBoardHeight;

    const scaleFactor = Math.min(widthRatio, heightRatio, MAXIMUM_SCALING_FACTOR);

    // TRANSLATION
    const centerOfCanvasViewportX = windowWidth / 2;
    const centerOfCanvasViewportY = canvasContainerRect.height / 2;
    const centerOfElementsX = totalBoardWidth / 2;
    const centerOfElementsY = totalBoardHeight / 2;

    const translationX = Math.round(
        centerOfCanvasViewportX - centerOfElementsX - elementBoundingRect.left + boardPadding,
    );
    const translationY = Math.round(
        centerOfCanvasViewportY - centerOfElementsY - elementBoundingRect.top + boardPadding,
    );

    scrollableContainer.style.position = 'absolute';
    scrollableContainer.style.background = 'none';
    scrollableContainer.style.transform = `translate3d(${translationX}px, ${translationY}px, 0) scale(${scaleFactor})`;
    scrollableContainer.style.transformOrigin = `${transformOriginX}px ${transformOriginY}px`;
    scrollableContainer.style.transition = 'transform .6s cubic-bezier(0.645, 0.045, 0.355, 1)';

    return {
        scaleFactor,
        elementBoundingRect,
    };
};

export const cleanupOnScaleTransitionEnd = () => {
    const workspacePositioner = document.querySelector('.WorkspacePositioner');
    workspacePositioner.classList.remove('scale-animation');
};
